import React, {useEffect} from 'react'
import {PageHeader} from "../../../components/common/pageHeader/PageHeader";
import {KTIcon, useDebounce} from "../../../../_metronic/helpers";
import {LoaderSection} from "../../../components/sections/loader/Loader";
import {EmptySection} from "../../../components/sections/empty/Empty";
import {PLACEHOLDER_IMAGE_URL} from "../../../mock/constants";
import phoneFormatter from "../../../utils/phoneFormatter";
import {MyTooltip} from "../../../components/common/tooltip/Tooltip";
import {Pagination} from "../../../components/common/pagination/Pagination";
import {useSearchParams} from "react-router-dom";
import {useQuery} from "react-query";
import {getUsers, updateUser} from "../../../services/userSvc";
import {useDeleteModal} from "../../../context/DeleteModalContext";
import {MODERATOR_USER} from "../../../mock/URLs";
import {separateFormatter} from "../../../utils/separateFormatter";
import imgBaseUrl from "../../../utils/imgBaseUrl";
import {roles} from "../../../mock/roles";
import {UserRole} from "../../../types/role";
import {useAuth} from "../../../modules/auth";

export const UsersPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const page = parseInt(searchParams.get("page") || "1", 10)
  const searchQuery = searchParams.get("search") || ""
  const debouncedSearch = useDebounce(searchQuery, 500);
  const {currentUser} = useAuth()

  const {
    data: usersData,
    isLoading: usersIsLoading,
    refetch: refetchUsers
  } = useQuery(["getUsers", page, debouncedSearch], () => getUsers({
    params: {
      page,
      search: debouncedSearch
    }
  }), {
    keepPreviousData: true
  })

  const totalItems = usersData?.total_items || 0;
  const itemsPerPage = usersData?.page_size || 8;

  const {openDeleteModal} = useDeleteModal()
  const handleDeleteClick = (id: number) => {
    openDeleteModal(MODERATOR_USER, id, refetchUsers);
  };

  const handleChangeStatus = async (id: number, status: boolean) => {
    try {
      await updateUser(id, {is_active: status})
      refetchUsers()
    } catch (e) {
      throw e
    }
  }

  const handleChangeRole = async (id: number, role: UserRole) => {
    try {
      await updateUser(id, {role: role})
      refetchUsers()
    } catch (e) {
      throw e
    }
  }

  const pageDetails = {
    title: "Foydalanuvchilar",
    desc: `Jami yangiliklar soni: (${separateFormatter(usersData?.total_items || 0, " ", "ta")})`,
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchParams({page: "1", search: query});
  }

  useEffect(() => {
    if (debouncedSearch) {
      refetchUsers();
    }
  }, [debouncedSearch, refetchUsers]);

  return (
      <div>
        <PageHeader pageDetails={pageDetails}/>
        <div className={"d-flex flex-wrap justify-content-between align-items-center rounded py-1 mb-5"}>
          <div className={"w-100 w-100 w-md-250px"}>
            <div className="d-flex align-items-center position-relative my-1">
              <KTIcon iconName={"magnifier"} className={"fs-1 position-absolute ms-3"}/>
              <input value={searchQuery}
                     onChange={handleSearchChange} type="text" data-kt-user-table-filter="search"
                     className="form-control w-100 w-md-250px ps-12"
                     placeholder="Qidiruv..."/>
            </div>
          </div>
        </div>

        {usersIsLoading ? <LoaderSection height={"50vh"}/> : usersData?.data.length === 0 ?
            <EmptySection height={"50vh"}/> : <div>
              <div className={`card`}>
                <div className='card-body py-3'>
                  <div className='table-responsive'>
                    <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                      <thead>
                      <tr className='fw-bold text-muted'>
                        <th className='min-w-150px'>Foydalanuvchi</th>
                        <th className='min-w-120px'>Login turi</th>
                        <th className='min-w-120px'>Statistika</th>
                        <th className='min-w-100px text-end'>Amallar</th>
                      </tr>
                      </thead>
                      <tbody>
                      {
                        usersData?.data?.map((user) => (
                            <tr key={user?.id}>
                              <td>
                                <div className='d-flex align-items-center'>
                                  <div className='symbol symbol-45px me-5'>
                                    <img className={"object-fit-cover"}
                                         src={user?.avatar ? imgBaseUrl(user?.avatar!) : PLACEHOLDER_IMAGE_URL}
                                         alt={`${user?.first_name} ${user?.last_name}`}/>
                                  </div>
                                  <div className='d-flex justify-content-start flex-column'>
                            <span className='text-dark fw-bold text-hover-primary fs-6'>
                              {user?.first_name} {user?.last_name}
                            </span>
                                    <span className='text-muted fw-semibold text-muted d-block fs-7'>
                        {user?.role?.toUpperCase()}
                      </span>
                                  </div>
                                </div>
                              </td>
                              <td>
                                {user?.register_type === "GOOGLE" ? <span className='badge badge-light fs-8 fw-bold'>
                    <KTIcon iconName={"google"} className={"pe-1"}/>{user?.email}</span>
                                    :
                                    <span className='badge badge-light fs-8 fw-bold'>
                    <KTIcon iconName={"simcard"} className={"pe-1"}/>
                                      {phoneFormatter(user?.phone!)}
              </span>
                                }
                              </td>

                              <td>
                                <div className={"d-flex gap-1"}>
                                  <MyTooltip title={"Xavfli zonadaman"} position={"top"}>
                                  <span
                                      className="badge badge-light-danger fs-8 fw-bold">{user?.sos_count}</span>
                                  </MyTooltip>
                                </div>
                              </td>
                              <td>
                                <div className='d-flex justify-content-end flex-shrink-0'>
                                  {currentUser?.role === "SUPER_ADMIN" &&
                                    <select onChange={(e) => handleChangeRole(
                                        user?.id, e.target.value as UserRole
                                    )} className={"form-select form-select-sm w-150px form-select-solid me-1"}
                                            name="role" id="">
                                      {roles.map((role) => (
                                          <option key={role.key} selected={
                                            role.key === user?.role
                                          } value={role.key}>{role.label}</option>
                                      ))}
                                    </select>
                                  }

                                  {user?.is_active ? <MyTooltip title={"Bloklash"} position={"top"}>
                                        <button
                                            onClick={() => handleChangeStatus(user?.id, !user?.is_active)}
                                            type={"button"}
                                            className='btn btn-icon btn-light-primary btn-sm me-1'
                                        >
                                          <KTIcon iconName='shield-cross' className='fs-3'/>
                                        </button>
                                      </MyTooltip> :
                                      <MyTooltip title={"Blokdan chiqarish"} position={"top"}>
                                        <button
                                            type={"button"}
                                            onClick={() => handleChangeStatus(user?.id, !user?.is_active)}
                                            className='btn btn-icon btn-light-danger btn-sm me-1'
                                        >
                                          <KTIcon iconName='shield-tick' className='fs-3'/>
                                        </button>
                                      </MyTooltip>
                                  }
                                  <MyTooltip title={"O'chirish"} position={"top"}>
                                    <button
                                        onClick={() => handleDeleteClick(user?.id)}
                                        type={"button"}
                                        className='btn btn-icon btn-danger btn-sm'
                                    >
                                      <KTIcon iconName='trash' className='fs-3'/>
                                    </button>
                                  </MyTooltip>
                                </div>
                              </td>
                            </tr>
                        ))
                      }
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className={"mt-10"}>
                <Pagination
                    totalItems={totalItems}
                    itemsPerPage={itemsPerPage}
                />
              </div>
            </div>}
      </div>
  )
}
